import { Injectable } from '@angular/core';
import moment from 'moment';
import { DateHelperService } from '../../../services/date-helper.service';
import { Asset } from '../../asset-list/models/asset.model';

@Injectable()
export class AssetExportDataService {
  constructor(private readonly dateHelperService: DateHelperService) {}

  public flattenAssets(assets: any[]): any[] {
    if (!assets?.length) {
      return [];
    }
    return assets.flatMap((item: any) => {
      const itemRecord: Partial<Asset> & { id?: string } = item as Partial<Asset> & { id?: string };
      const variants: Array<Partial<Asset> & { parentId?: string }> =
        itemRecord.subAsset?.map((variant: { id?: string }) => ({ ...variant, parentId: itemRecord.id })) ?? [];
      return [{ ...itemRecord }, ...variants];
    });
  }

  public getPdfData(flattenAssets: any[]): any[] {
    return flattenAssets.map((item: any) => this.transformToPdfFormat(item));
  }

  private transformToPdfFormat(item: any): Record<string, any> {
    const itemRecord: Partial<Asset> & {
      parentId?: string;
      releaseDate?: string | { year: number; month: number; day: number };
    } = item as Partial<Asset> & {
      parentId?: string;
      releaseDate?: string | { year: number; month: number; day: number };
    };

    return {
      asset: itemRecord.title || itemRecord.detail || '',
      type: itemRecord.parentId ? 'TECH. VARIANT' : 'Master',
      parentId: itemRecord.parentId || null,
      categories: itemRecord.categories?.join(', ') || '-',
      bets: itemRecord.bets || [],
      funnel: itemRecord.funnel || '-',
      focus: itemRecord.focus || '-',
      region: itemRecord.region || '-',
      agency: itemRecord.agency || '-',
      expandable: (itemRecord?.subAsset?.length ?? 0) > 0,
      releaseDate: this.formatReleaseDate(itemRecord.releaseDate),
      length: itemRecord.length || [],
      assetType: itemRecord.type || '-',
      format: itemRecord.format?.join(', ') || '-',
    };
  }

  private formatReleaseDate(releaseDate?: string | { year: number; month: number; day: number }): string {
    if (!releaseDate) {
      return '-';
    }
    if (typeof releaseDate === 'string') {
      return moment(this.dateHelperService.fromDatePickerToDate(releaseDate)).format('YYYY-MM-DD');
    }
    return moment(
      this.dateHelperService.fromDatePickerToDate(
        `${releaseDate.year}-${String(releaseDate.month).padStart(2, '0')}-${String(releaseDate.day).padStart(2, '0')}`
      )
    ).format('YYYY-MM-DD');
  }

  public getExportData(flattenAssets: any[]): Record<string, string>[] {
    return flattenAssets.map((item: any) => this.transformToExportFormat(item));
  }

  private transformToExportFormat(item: any): Record<string, string> {
    const itemRecord: Partial<Asset> & {
      parentId?: string;
      releaseDate?: string | { year: number; month: number; day: number };
    } = item as Partial<Asset> & {
      parentId?: string;
      releaseDate?: string | { year: number; month: number; day: number };
    };

    return {
      Asset: itemRecord.title || itemRecord.detail || '',
      Type: itemRecord.parentId ? 'TECH. VARIANT' : 'Master',
      Categories: itemRecord.categories?.join(', ') || '-',
      Bets: itemRecord.bets?.join(', ') || '-',
      Funnel: itemRecord.funnel || '-',
      Focus: itemRecord.focus || '-',
      'Release Date': this.formatReleaseDate(itemRecord.releaseDate),
      Length: itemRecord.length?.join(', ') || '-',
      'Asset Type': itemRecord.type || '-',
      Format: itemRecord.format?.join(', ') || '-',
    };
  }

  public convertToSheetData(data: Record<string, string>[]): string[][] {
    const sheetData: string[][] = [];
    if (data.length === 0) {
      return sheetData;
    }
    const headers: string[] = Object.keys(data[0]);
    sheetData.push(headers);
    for (const item of data) {
      const row: string[] = headers.map((header: string) => {
        const value: string | undefined = item[header];
        if (value === null || value === undefined) {
          return '';
        }
        return String(value);
      });
      sheetData.push(row);
    }
    return sheetData;
  }

  public convertToCsvContent(data: Record<string, string>[]): string {
    if (data.length === 0) {
      return '';
    }
    const headers: string[] = Object.keys(data[0]);
    const csvRows: string[] = [
      headers.join(','),
      ...data.map((row: Record<string, string>) =>
        headers
          .map((header: string) => {
            const cell: string = row[header]?.toString() || '';
            return cell.includes(',') ? `"${cell}"` : cell;
          })
          .join(',')
      ),
    ];
    return csvRows.join('\n');
  }
}
